import { Link } from 'react-router-dom';
import type { Course } from '@/types/course';
import { formatCount } from '@/utils/course';
import { useFavorites } from '@/hooks/useFavorites';

interface CourseCardProps {
  course: Course;
  showFavorite?: boolean;
}

export default function CourseCard({ course, showFavorite = true }: CourseCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(course.id);

  return (
    <article className="group relative flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden transition-all duration-300 hover:border-primary-300 hover:-translate-y-1">
      <Link to={`/detail/${course.id}`} className="block cursor-pointer">
        <div className="relative w-full aspect-[8/5] overflow-hidden bg-background-200">
          <img
            src={course.cover}
            alt={course.title}
            title={`${course.title} · 秘密教学课程`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
            <span
              className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${
                course.priceType === '免费'
                  ? 'bg-secondary-500 text-background-50'
                  : 'bg-accent-500 text-background-50'
              }`}
            >
              {course.priceType === '免费' ? '免费' : `¥${course.price}`}
            </span>
            <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-primary-950/70 text-background-50">
              {course.level}
            </span>
          </div>
        </div>
      </Link>

      {showFavorite && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            toggleFavorite(course.id);
          }}
          aria-label={favorited ? '取消收藏' : '收藏课程'}
          className={`absolute top-2.5 right-2.5 w-8 h-8 flex items-center justify-center rounded-full text-lg transition-colors cursor-pointer ${
            favorited
              ? 'bg-accent-500 text-background-50'
              : 'bg-background-50/85 text-foreground-600 hover:text-accent-500'
          }`}
        >
          <i className={favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
        </button>
      )}

      <div className="flex flex-col flex-1 p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 font-medium">
            {course.categoryName}
          </span>
          <span className="text-[11px] text-foreground-500">{course.subcategory}</span>
        </div>

        <h3 className="font-heading font-bold text-base text-foreground-950 leading-snug mb-1.5 line-clamp-2">
          <Link to={`/detail/${course.id}`} className="cursor-pointer hover:text-primary-600 transition-colors">
            {course.title}
          </Link>
        </h3>

        <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-3">
          {course.intro}
        </p>

        <div className="flex items-center gap-2 mb-3">
          <span className="w-6 h-6 rounded-full overflow-hidden bg-background-200 shrink-0">
            <img src={course.teacherAvatar} alt={course.teacher} className="w-full h-full object-cover" />
          </span>
          <span className="text-xs text-foreground-700">{course.teacher}</span>
        </div>

        <div className="mt-auto pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <span className="w-4 h-4 flex items-center justify-center text-sm">
              <i className="ri-user-3-line"></i>
            </span>
            {formatCount(course.students)} 人在学
          </span>
          <span className="flex items-center gap-1 text-accent-600 font-medium">
            <span className="w-4 h-4 flex items-center justify-center text-sm">
              <i className="ri-star-fill"></i>
            </span>
            {course.rating.toFixed(1)}
          </span>
        </div>
      </div>
    </article>
  );
}